/**
 * Eufy Settings Admin Page
 *
 * Allows administrators to configure:
 * - Eufy API credentials
 * - Locker device mappings
 * - Test connections
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface EufySettingsData {
  api_key: string;
  api_secret: string;
  base_url: string;
  has_api_key: boolean;
  has_api_secret: boolean;
}

interface Locker {
  id: number;
  number: number;
  name: string;
  eufy_device_id: string;
  has_device_id: boolean;
}

const API_BASE = '/api';

const EufySettings: React.FC = () => {
  const { user } = useAuth();

  const [settings, setSettings] = useState<EufySettingsData | null>(null);
  const [lockers, setLockers] = useState<Locker[]>([]);
  const [isConfigured, setIsConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://api.eufylife.com');

  // Locker editing
  const [editingLocker, setEditingLocker] = useState<number | null>(null);
  const [lockerDeviceId, setLockerDeviceId] = useState('');

  useEffect(() => {
    if (user?.is_superuser) {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/admin/settings/eufy/`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load settings');
      const data = await response.json();
      setSettings(data.settings);
      setLockers(data.lockers);
      setIsConfigured(data.is_configured);
      setBaseUrl(data.settings.base_url || 'https://api.eufylife.com');
    } catch (err) {
      console.error('Failed to load settings:', err);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const saveCredentials = async () => {
    if (!apiKey && !apiSecret && !baseUrl) {
      setMessage({ type: 'error', text: 'Please enter at least one value to update' });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      const body: any = {};
      if (apiKey) body.api_key = apiKey;
      if (apiSecret) body.api_secret = apiSecret;
      if (baseUrl) body.base_url = baseUrl;

      const response = await fetch(`${API_BASE}/admin/settings/eufy/update/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save settings');
      }

      setMessage({ type: 'success', text: data.message });
      setApiKey('');
      setApiSecret('');
      loadSettings();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      setMessage(null);

      const response = await fetch(`${API_BASE}/admin/settings/eufy/test/`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setTesting(false);
    }
  };

  const saveLockerDevice = async (lockerId: number) => {
    try {
      const response = await fetch(`${API_BASE}/admin/settings/lockers/${lockerId}/device/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ eufy_device_id: lockerDeviceId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save');
      }

      setMessage({ type: 'success', text: `Locker ${data.locker.number} device ID updated` });
      setEditingLocker(null);
      setLockerDeviceId('');
      loadSettings();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const testLocker = async (lockerId: number) => {
    try {
      const response = await fetch(`${API_BASE}/admin/settings/lockers/${lockerId}/test/`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: `Test passcode: ${data.test_passcode} (valid until ${data.valid_until})` });
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  if (!user?.is_superuser) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <h1 style={styles.title}>Access Denied</h1>
          <p>You must be a superuser to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.title}>Eufy Smart Lock Settings</h1>
        <p style={styles.subtitle}>
          Configure API credentials and locker device mappings for the Eufy smart lock integration.
        </p>

        {message && (
          <div style={message.type === 'success' ? styles.successMsg : styles.errorMsg}>
            {message.text}
          </div>
        )}

        {loading ? (
          <p style={styles.loading}>Loading settings...</p>
        ) : (
          <>
            {/* API Credentials Section */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>API Credentials</h2>

              <div style={styles.statusRow}>
                <span style={styles.statusLabel}>Status:</span>
                <span style={isConfigured ? styles.statusConfigured : styles.statusNotConfigured}>
                  {isConfigured ? 'Configured' : 'Not Configured'}
                </span>
              </div>

              {settings?.has_api_key && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Current API Key:</span>
                  <span style={styles.maskedValue}>{settings.api_key}</span>
                </div>
              )}

              {settings?.has_api_secret && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Current API Secret:</span>
                  <span style={styles.maskedValue}>{settings.api_secret}</span>
                </div>
              )}

              <div style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>API Key (leave blank to keep current):</label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder="Enter new API key..."
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>API Secret (leave blank to keep current):</label>
                  <input
                    type="password"
                    value={apiSecret}
                    onChange={e => setApiSecret(e.target.value)}
                    placeholder="Enter new API secret..."
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Base URL:</label>
                  <input
                    type="text"
                    value={baseUrl}
                    onChange={e => setBaseUrl(e.target.value)}
                    placeholder="https://api.eufylife.com"
                    style={styles.input}
                  />
                </div>

                <div style={styles.buttonRow}>
                  <button
                    style={styles.saveBtn}
                    onClick={saveCredentials}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Credentials'}
                  </button>

                  <button
                    style={styles.testBtn}
                    onClick={testConnection}
                    disabled={testing || !isConfigured}
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </button>
                </div>
              </div>
            </div>

            {/* Locker Mappings Section */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Locker Device Mappings</h2>
              <p style={styles.sectionDescription}>
                Map each physical locker to its Eufy smart lock device ID.
              </p>

              <div style={styles.lockerList}>
                {lockers.length === 0 ? (
                  <p style={styles.emptyText}>No lockers found. Add lockers in the store management section first.</p>
                ) : (
                  lockers.map(locker => (
                    <div key={locker.id} style={styles.lockerItem}>
                      <div style={styles.lockerInfo}>
                        <span style={styles.lockerNumber}>Locker #{locker.number}</span>
                        <span style={styles.lockerName}>{locker.name}</span>
                      </div>

                      {editingLocker === locker.id ? (
                        <div style={styles.lockerEdit}>
                          <input
                            type="text"
                            value={lockerDeviceId}
                            onChange={e => setLockerDeviceId(e.target.value)}
                            placeholder="Eufy Device ID..."
                            style={styles.lockerInput}
                          />
                          <button
                            style={styles.lockerSaveBtn}
                            onClick={() => saveLockerDevice(locker.id)}
                          >
                            Save
                          </button>
                          <button
                            style={styles.lockerCancelBtn}
                            onClick={() => {
                              setEditingLocker(null);
                              setLockerDeviceId('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={styles.lockerActions}>
                          <span style={locker.has_device_id ? styles.deviceIdSet : styles.deviceIdNotSet}>
                            {locker.eufy_device_id || 'Not configured'}
                          </span>
                          <button
                            style={styles.editBtn}
                            onClick={() => {
                              setEditingLocker(locker.id);
                              setLockerDeviceId(locker.eufy_device_id || '');
                            }}
                          >
                            Edit
                          </button>
                          {locker.has_device_id && (
                            <button
                              style={styles.testLockerBtn}
                              onClick={() => testLocker(locker.id)}
                            >
                              Test
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '2rem',
  },
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    color: '#666',
    margin: '0 0 1.5rem 0',
    fontSize: '0.95rem',
  },
  successMsg: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
  },
  errorMsg: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
  },
  loading: {
    textAlign: 'center',
    color: '#666',
    padding: '2rem',
  },
  section: {
    marginBottom: '2rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #eee',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 0.75rem 0',
  },
  sectionDescription: {
    color: '#666',
    fontSize: '0.9rem',
    margin: '0 0 1rem 0',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  statusLabel: {
    fontWeight: '500',
    color: '#333',
  },
  statusConfigured: {
    color: '#28a745',
    fontWeight: '600',
  },
  statusNotConfigured: {
    color: '#dc3545',
    fontWeight: '600',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
  },
  infoLabel: {
    color: '#666',
  },
  maskedValue: {
    fontFamily: 'monospace',
    backgroundColor: '#f5f5f5',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
  },
  form: {
    marginTop: '1rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: '#333',
    fontSize: '0.9rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  buttonRow: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  saveBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.95rem',
  },
  testBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#0066cc',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.95rem',
  },
  lockerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  lockerItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #eee',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  lockerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  lockerNumber: {
    fontWeight: '600',
    color: '#333',
  },
  lockerName: {
    fontSize: '0.85rem',
    color: '#666',
  },
  lockerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  deviceIdSet: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
  },
  deviceIdNotSet: {
    fontSize: '0.85rem',
    color: '#999',
    fontStyle: 'italic',
  },
  editBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  testLockerBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    color: '#1565c0',
  },
  lockerEdit: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flex: 1,
    minWidth: '200px',
  },
  lockerInput: {
    flex: 1,
    padding: '0.5rem',
    border: '2px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontFamily: 'monospace',
  },
  lockerSaveBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  lockerCancelBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#999',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  emptyText: {
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '1rem',
  },
};

export default EufySettings;
